// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { isValidCategorySlug, getCategoryBySlug } from '@/config/categories';
import { getAllComedianSlugs } from '@/data/comedians';
import { getAllArticleSlugs } from '@/data/articles';
import { trackPageView } from '../services/analyticsService';
import { addHreflangTags, updateHtmlLang } from '@/utils/hreflang';

// Lazy-loaded views for code splitting
const LangLayout = () => import(/* webpackChunkName: "lang-layout" */ '../layouts/LangLayout.vue');
const JokesFeedView = () => import(/* webpackChunkName: "feed" */ '../views/JokesFeedView.vue');
const SpotlightView = () => import(/* webpackChunkName: "spotlight" */ '../views/SpotlightView.vue');
const JokeView = () => import(/* webpackChunkName: "joke" */ '../views/JokeView.vue');
const CategoriesView = () => import(/* webpackChunkName: "categories" */ '../views/CategoriesView.vue');
const CategoryView = () => import(/* webpackChunkName: "category" */ '../views/CategoryView.vue');
const SubmitView = () => import(/* webpackChunkName: "submit" */ '../views/SubmitView.vue');
const AboutView = () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue');
const VideosView = () => import(/* webpackChunkName: "videos" */ '../views/VideosView.vue');
const BlogView = () => import(/* webpackChunkName: "blog" */ '../views/BlogView.vue');
const NotFoundView = () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue');
const LegalView = () => import(/* webpackChunkName: "legal" */ '../views/LegalView.vue');

const SUPPORTED_LANGS = ['en', 'fr', 'es', 'ar'];

const routes = [
  // Root redirect: / → /{savedLang} or /en
  {
    path: '/',
    redirect: () => {
      try {
        const saved = localStorage.getItem('selectedLanguages');
        if (saved) {
          const langs = JSON.parse(saved);
          if (langs[0] && SUPPORTED_LANGS.includes(langs[0])) return `/${langs[0]}`;
        }
      } catch { /* ignore */ }
      return '/en';
    }
  },

  // ============================================
  // LANGUAGE-PREFIXED ROUTES
  // ============================================
  {
    path: '/:lang(en|fr|es|ar)',
    component: LangLayout,
    children: [
      {
        path: '',
        name: 'home',
        component: JokesFeedView,
        meta: { title: 'Humoraq - Jokes Feed' }
      },
      {
        path: 'spotlight',
        name: 'spotlight',
        component: SpotlightView,
        meta: { title: 'Humoraq - Spotlight' }
      },

      // Blog routes
      {
        path: 'blogs',
        name: 'blogs',
        component: BlogView,
        meta: {
          title: 'Famous Comedians Biographies - Humoraq Blog',
          description: 'Explore biographies of legendary comedians'
        }
      },
      {
        path: 'blogs/:slug',
        name: 'blog-comedian',
        component: BlogView,
        props: true,
        beforeEnter: (to, from, next) => {
          const slug = to.params.slug;
          const validSlugs = [...getAllComedianSlugs(), ...getAllArticleSlugs()];
          if (validSlugs.includes(slug)) {
            next();
          } else {
            next({ name: 'blogs', params: { lang: to.params.lang } });
          }
        },
        meta: { title: 'Comedian Biography - Humoraq Blog' }
      },

      // SEO-friendly joke URL: /{lang}/{category}-jokes/{title-slug}-{id}
      {
        path: ':categorySlug-jokes/:titleSlugWithId',
        name: 'joke-seo',
        component: JokeView,
        props: route => {
          const titleSlugWithId = route.params.titleSlugWithId;
          const lastHyphenIndex = titleSlugWithId.lastIndexOf('-');
          const id = lastHyphenIndex !== -1
            ? titleSlugWithId.substring(lastHyphenIndex + 1)
            : titleSlugWithId;
          return {
            id: id,
            categorySlug: route.params.categorySlug,
            titleSlug: titleSlugWithId
          };
        },
        beforeEnter: (to, from, next) => {
          const categorySlug = to.params.categorySlug;
          if (isValidCategorySlug(categorySlug)) {
            const category = getCategoryBySlug(categorySlug);
            to.meta.title = `${category.label} Joke - Humoraq`;
            next();
          } else {
            next({ name: 'not-found', params: { lang: to.params.lang } });
          }
        },
        meta: { title: 'Joke Details - Humoraq' }
      },

      // Categories
      {
        path: 'categories',
        name: 'categories',
        component: CategoriesView,
        meta: { title: 'Browse Categories - Humoraq' }
      },
      {
        path: 'category/:slug',
        name: 'category',
        component: CategoryView,
        props: route => ({ slug: route.params.slug }),
        beforeEnter: (to, from, next) => {
          const slug = to.params.slug;
          if (isValidCategorySlug(slug)) {
            const category = getCategoryBySlug(slug);
            to.meta.title = `${category.label} Jokes - Humoraq`;
            to.meta.description = category.description;
            next();
          } else {
            next({ name: 'categories', params: { lang: to.params.lang } });
          }
        },
        meta: { title: 'Category - Humoraq' }
      },

      // Other pages
      {
        path: 'submit',
        name: 'submit',
        component: SubmitView,
        meta: { title: 'Submit a Joke - Humoraq' }
      },
      {
        path: 'about',
        name: 'about',
        component: AboutView,
        meta: { title: 'About Us - Humoraq' }
      },
      {
        path: 'videos',
        name: 'videos',
        component: VideosView,
        meta: { title: 'Funny Videos - Humoraq' }
      },
      {
        path: 'legal',
        name: 'legal',
        component: LegalView,
        meta: { title: 'Privacy & Terms - Humoraq' }
      },

      // 404 within language scope
      {
        path: ':pathMatch(.*)',
        name: 'not-found',
        component: NotFoundView,
        meta: { title: '404 - Humoraq' }
      }
    ]
  },

  // ============================================
  // LEGACY REDIRECTS (old URLs without lang prefix → /en/...)
  // ============================================
  { path: '/feed', redirect: '/en' },
  { path: '/spotlight', redirect: '/en/spotlight' },
  { path: '/categories', redirect: '/en/categories' },
  { path: '/category/:slug', redirect: to => `/en/category/${to.params.slug}` },
  { path: '/blogs/:slug', redirect: to => `/en/blogs/${to.params.slug}` },
  { path: '/blogs', redirect: '/en/blogs' },
  { path: '/videos', redirect: '/en/videos' },
  { path: '/submit', redirect: '/en/submit' },
  { path: '/about', redirect: '/en/about' },
  { path: '/legal', redirect: '/en/legal' },
  { path: '/joke/:id', redirect: to => `/en/general-jokes/${to.params.id}` },
  { path: '/joke-about-:categorySlug/:id', redirect: to => `/en/${to.params.categorySlug}-jokes/${to.params.id}` },
  { path: '/video/:id', redirect: '/en/videos' },
  // New-format joke URLs without lang prefix
  { path: '/:categorySlug-jokes/:titleSlugWithId', redirect: to => `/en/${to.params.categorySlug}-jokes/${to.params.titleSlugWithId}` },

  // Global 404 catch-all
  { path: '/:pathMatch(.*)*', redirect: '/en' }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  }
});

// Strip trailing slashes + sync language from URL to store
router.beforeEach((to, from, next) => {
  // Strip trailing slashes
  if (to.path !== '/' && to.path.endsWith('/')) {
    next({ path: to.path.slice(0, -1), query: to.query, hash: to.hash, replace: true });
    return;
  }

  // Sync language from URL to Vuex store
  const lang = to.params.lang;
  if (lang && SUPPORTED_LANGS.includes(lang)) {
    const currentStoreLang = store.getters['preferences/selectedLanguage'];
    if (currentStoreLang !== lang) {
      store.dispatch('preferences/setLanguages', [lang]);
    }
  }

  // For joke pages the component sets the title async after loading from Firestore,
  // so don't overwrite the server-injected <title> here with the generic placeholder.
  if (to.name !== 'joke-seo') {
    document.title = to.meta.title || 'Humoraq';
  }
  next();
});

// Track page views, update hreflang and canonical
router.afterEach((to) => {
  trackPageView(to.name, to.meta.title || 'Humoraq');

  const lang = to.params.lang || 'en';

  // Update hreflang tags for multilingual SEO
  addHreflangTags(to.path);
  updateHtmlLang(lang);

  // Update canonical URL: https://humoraq.com/{lang}{pathWithoutLang}
  const pathWithoutLang = to.path.replace(/^\/(en|fr|es|ar)/, '') || '';
  const canonical = `https://humoraq.com/${lang}${pathWithoutLang}`;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', canonical);
});

export default router;
